<script setup>
import { computed, ref, watchEffect } from 'vue';
import { useSignUpModuleStore } from '@/stores/modules/signup/signUpStore';
import { storeToRefs } from 'pinia/dist/pinia';
import AddressResult from '@/modules/Signup/components/address/AddressResult.vue';
import { debounce } from '@/modules/Signup/utils';
import { useLoadingState } from '@/composables/useLoadingState';
import AddressLoader from '@/modules/Signup/components/address/AddressLoader.vue';
import HomeSearchIcon from '@/modules/Signup/__clients/ezi/icons/home-search.svg';
import { useModals } from '@/modules/Signup/composables/useModals';
import VModal from '@/modules/Signup/components/VModal.vue';

const
    signUpStore = useSignUpModuleStore(),
    {
        address,
        optAddresses,
        selectedAddressDto,
        searchResultTextLoader,
        searchResultMessage,
        locationEligibility,
        optNbnLocations,
        showMultipleNBNLocations,
        optOrderingOptions,
        selectedOrderingOption,
        selectedNbnLocation,
    } = storeToRefs(signUpStore);

const { isLoading, icon, withLoading } = useLoadingState();
const { open, close, isOpen } = useModals();

const addressSearch = ref('');
const showSuggestions = computed(() => (
    addressSearch.value.length > 0 && addressSearch.value !== address.value?.FormattedAddress
));

let currentAbortController = null;
const searchCompleted = ref(false);

// Debounce search
const handleDebouncedSearch = async () => {
    // If the search bar is empty, clear everything and return early
    if (!addressSearch.value) {
        // Abort any ongoing request
        if (currentAbortController) {
            currentAbortController.abort();
            currentAbortController = null;
        }

        // Clear state
        optAddresses.value = [];
        searchCompleted.value = false;
        signUpStore.clearLocationEligibility();
        return;
    }

    /**
     * Abort the previous request if there's a new request being a process, as it will affect the result being
     * generated.
     */
    if (currentAbortController) {
        currentAbortController.abort();
    }

    currentAbortController = new AbortController();
    const signal = currentAbortController.signal;

    // Reset state AFTER creating a new controller but BEFORE starting search
    optAddresses.value = [];
    searchCompleted.value = false;
    signUpStore.setSearchResultTextLoader('Searching ...');

    try {
        await withLoading(async () => {
            // Set the loader again inside with loading
            if (!signal.aborted) {
                signUpStore.setSearchResultTextLoader('Searching ...');
            }

            await signUpStore.getAddress(addressSearch.value, signal);

            // Only update searchCompleted if this request wasn't aborted
            if (!signal.aborted) {
                searchCompleted.value = true;
            }
        });
    } catch (error) {
        if (error.name === 'AbortError') {
            console.log('Previous search aborted...');
        } else {
            console.error('Search error: ', error);
            // Mark as completed even on error
            searchCompleted.value = true;
        }
    } finally {
        /*// Always clear the searching state when done (whether aborted or completed)
        if (!signal.aborted) {
            isSearching.value = false;
        }*/
    }
};
const onSearchInput = debounce(handleDebouncedSearch, 800);

const selectAddress = async (selectedAddress) => {
    // Reset
    await signUpStore.clearLocationEligibility();

    address.value = selectedAddress;
    addressSearch.value = selectedAddress.FormattedAddress;
    showSuggestions.value = false;
    selectedAddressDto.value = selectedAddress;

    await withLoading(async () => {
        await signUpStore.checkLocationEligibility();
    });
};

watchEffect(() => {
    /** Persist data from the selected address */
    if (address.value) {
        addressSearch.value = address.value.FormattedAddress;
    }

    /** Trigger the modal that holds the NBNLocation */
    if (showMultipleNBNLocations.value) {
        open('optNbnLocations');
    }
});

/**
 * Multiple NBNLocation
 */
const selectNBNLocation = async (selectedOption) => {
    selectedNbnLocation.value = selectedOption;
    // Closing the modal as there's an actual loader in the background, to avoid confusion.
    close('optNbnLocations');
    await withLoading(async () => {
        await signUpStore.processOrderingOptions(selectedOption);
    });
};

/**
 * Ordering Option
 */
const selectOrderingOption = (selectedOption) => {
    selectedOrderingOption.value = selectedOption;
    close('optOrderingOptions');
};
</script>

<template>
    <div class="address-search">
        <InputGroup class="!tw-bg-[#FF7D901A] tw-py-4 tw-px-10">
            <InputGroupAddon class="!tw-bg-transparent tw-border-0">
                <img
                    :src="HomeSearchIcon"
                    class="!tw-h-[75px]"
                    alt=""
                />
            </InputGroupAddon>
            <InputText
                v-model="addressSearch"
                @input="onSearchInput"
                type="search"
                placeholder="Eg.  Unit XX, X Name Street, Suburb, State, XXXX"
                class="tw-bg-transparent tw-border-0 placeholder:tw-text-[#312C7080] !tw-font-normal !tw-ml-6"
            />
            <InputGroupAddon class="!tw-bg-transparent !tw-border-0">
                <i class="pi pi-search !tw-font-extrabold tw-text-[#312C70] tw-text-4xl"></i>
            </InputGroupAddon>
        </InputGroup>

        <div class="address-search__result">
            <AddressLoader
                v-if="isLoading"
                :text="searchResultTextLoader"
                :icon="icon"
            />

            <ul v-if="!isLoading && showSuggestions && searchCompleted" class="tw-h-[255px] tw-overflow-y-auto">
                <template v-if="optAddresses.length > 0">
                    <li v-for="address in optAddresses" :key="address['PlaceId']" class="tw-flex">
                        <button @click.prevent="selectAddress(address)" class="tw-w-full tw-text-left">
                            {{ address['FormattedAddress'] }}
                        </button>
                    </li>
                </template>
                <template v-else>
                    <li class="tw-flex tw-items-center tw-justify-center !tw-h-full !tw-bg-transparent">
                        No records found!
                    </li>
                </template>
            </ul>

            <template v-if="addressSearch">
                <!-- Should only for one LOC, otherwise it will open a modal -->
                <AddressResult
                    v-if="locationEligibility.isEligible !== null"
                    :title="searchResultMessage.title"
                    :severity="searchResultMessage.severity"
                >
                    {{ searchResultMessage.detail }}
                </AddressResult>
            </template>
        </div>
    </div>

    <!--
        | =========================================
        |  Modal
        | =========================================
    -->
    <VModal v-model="isOpen('optNbnLocations').value">
        <p class="tw-text-3xl tw-mb-6">
            Found <strong>{{ Object.keys(optNbnLocations).length }} NBN Locations</strong>, kindly select one.
        </p>

        <ul class="tw-list-none tw-pl-0 tw-flex tw-flex-col tw-gap-4">
            <li v-for="(options, optionIndex) in optNbnLocations" :key="optionIndex">
                <Button
                    @click.prevent="selectNBNLocation(optionIndex)"
                    class="tw-w-full tw-text-center tw-text-2xl tw-py-4 tw-px-8 !tw-border-secondary !tw-text-secondary
                    hover:!tw-border-primary hover:!tw-text-white hover:!tw-bg-primary"
                    outlined
                >
                    {{ options }}
                </Button>
            </li>
        </ul>
    </VModal>

    <VModal v-model="isOpen('optOrderingOptions').value">
        <ul class="tw-list-none tw-pl-0">
            <li v-for="(options, optionIndex) in optOrderingOptions" :key="optionIndex">
                <button @click.prevent="selectOrderingOption(optionIndex)">
                    {{ options }}
                </button>
            </li>
        </ul>
    </VModal>
</template>

<style lang="scss" scoped>
/*TODO: refactor*/
.address-search__result {
    background: #312C7008;
    height: 255px;
    border-top: 1px solid #312C704D;

    li {
        background: #312C7008;
        height: 85px;
        border-top: 1px solid #312C704D;

        &:first-child {
            border-top: 0;
        }

        button {
            padding-left: calc(30px + 138.31px + 18px);
            padding-right: 30px;
        }
    }

    li:hover {
        background: #312C7012;
    }
}
</style>